import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'
import type { FormStatus } from '@/types'
import type { ProjectTypeValue } from '@/constants/site'

import { useId, useState } from 'react'
import { ArrowRight, CircleCheck, Globe, Smartphone } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonClasses } from '@/components/generals/button-link'
import { PROJECT_TYPES } from '@/constants/site'

import {
    cleanText,
    FIELD_LIMITS,
    isValidEmail,
    MESSAGE_MIN_LENGTH
} from '@/lib/form-validation'

type FieldName = 'name' | 'email' | 'message'

type FieldErrors = Partial<Record<FieldName, string>>

const EMPTY_VALUES: Record<FieldName, string> = { name: '', email: '', message: '' }

const FALLBACK_ERROR = 'Oops! Something went wrong while submitting the form.'

const PROJECT_ICONS = {
    website: Globe,
    app: Smartphone
} as const

const inputClasses = 'w-full rounded-control border border-white/20 bg-white/10 px-3.5 py-3 text-base leading-[1.3] text-white outline-none transition-[border-color,background-color] duration-300 placeholder:text-white/45 hover:border-white/35 focus:border-brand-orchid focus:bg-white/15 aria-[invalid=true]:border-[#ffb3c7]'

const validate = (values: Record<FieldName, string>): FieldErrors => {
    const errors: FieldErrors = {}

    if (!cleanText(values.name, FIELD_LIMITS.name)) errors.name = 'Please enter your name.'
    if (!isValidEmail(values.email.trim())) errors.email = 'Please enter a valid email address.'
    if (cleanText(values.message, FIELD_LIMITS.message).length < MESSAGE_MIN_LENGTH) errors.message = 'Please tell me a little more about your project.'

    return errors
}

export const ContactForm: FC = (): ReactNode => {
    const [values, setValues] = useState(EMPTY_VALUES)
    const [projectType, setProjectType] = useState<ProjectTypeValue>('website')
    const [errors, setErrors] = useState<FieldErrors>({})
    const [status, setStatus] = useState<FormStatus>('idle')
    const [formError, setFormError] = useState<string | null>(null)
    const baseId = useId()

    const fieldId = (name: string): string => `${baseId}-${name}`

    const onChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        const name = event.target.name as FieldName

        setValues(current => ({ ...current, [name]: event.target.value }))

        if (errors[name]) setErrors(current => ({ ...current, [name]: undefined }))
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (status === 'submitting') return

        const honeypot = event.currentTarget.elements.namedItem('company') as HTMLInputElement | null
        const nextErrors = validate(values)

        setErrors(nextErrors)
        setFormError(null)

        if (Object.keys(nextErrors).length > 0) {
            const firstInvalid = (Object.keys(nextErrors) as Array<FieldName>)[0]

            document.getElementById(fieldId(firstInvalid))?.focus()

            return
        }

        setStatus('submitting')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...values, projectType, company: honeypot?.value ?? '' })
            })

            const data = await response.json().catch(() => null) as { ok?: boolean, message?: string, fields?: FieldErrors } | null

            if (!response.ok || !data?.ok) {
                if (data?.fields) setErrors(data.fields)

                throw new Error(data?.message ?? FALLBACK_ERROR)
            }

            setValues(EMPTY_VALUES)
            setStatus('success')
        }
        catch (submitError) {
            setStatus('error')
            setFormError(submitError instanceof Error && submitError.message ? submitError.message : FALLBACK_ERROR)
        }
    }

    if (status === 'success') {
        return (
            <div role='status' className='flex w-full flex-col items-center gap-3 rounded-card bg-black/25 px-6 py-10 text-center shadow-[inset_0_0_0_1px_rgb(255_255_255/0.12)] backdrop-blur-sm animate-in fade-in zoom-in-95 duration-500'>
                <CircleCheck className='size-10 text-brand-orchid' strokeWidth={1.5} aria-hidden='true' />

                <p className='font-heading text-heading-5 font-medium'>
                    Thank you! Your submission has been received!
                </p>

                <p className='max-w-sm text-base leading-[1.3] text-white/75'>
                    I&apos;ll read your message and get back to you by email soon.
                </p>

                <button
                    type='button'
                    onClick={() => setStatus('idle')}
                    className='mt-2 rounded-control px-3 py-2 text-sm font-medium text-white/85 underline underline-offset-4 transition-colors duration-300 hover:text-white focus-visible:outline-2 focus-visible:outline-white'
                >
                    Send another message
                </button>
            </div>
        )
    }

    const errorFor = (name: FieldName): ReactNode => errors[name] && (
        <p id={`${fieldId(name)}-error`} className='mt-1.5 text-sm leading-[1.3] text-[#ffc6d4]'>
            {errors[name]}
        </p>
    )

    return (
        <form
            onSubmit={onSubmit}
            noValidate
            className='relative w-full rounded-card bg-black/25 p-5 text-left shadow-[inset_0_0_0_1px_rgb(255_255_255/0.12)] backdrop-blur-sm md:p-8'
        >
            <fieldset className='mb-6'>
                <legend className='mb-3 text-sm font-medium text-white/85'>
                    What can I build for you?
                </legend>

                <div className='grid gap-3 sm:grid-cols-2'>
                    {PROJECT_TYPES.map(type => {
                        const Icon = PROJECT_ICONS[type.value]
                        const checked = projectType === type.value

                        return (
                            <label
                                key={type.value}
                                className={cn(
                                    'flex cursor-pointer items-center justify-center gap-2 rounded-control px-4 py-3 text-base leading-[1.3] font-medium transition-[background-color,box-shadow] duration-300 has-[input:focus-visible]:outline-2 has-[input:focus-visible]:outline-offset-2 has-[input:focus-visible]:outline-white',
                                    checked
                                        ? 'bg-primary bg-linear-to-r from-primary to-brand-violet text-white shadow-button'
                                        : 'bg-white/10 text-white/85 shadow-[inset_0_0_0_1px_rgb(255_255_255/0.2)] hover:bg-white/15'
                                )}
                            >
                                <input
                                    type='radio'
                                    name='projectType'
                                    value={type.value}
                                    checked={checked}
                                    onChange={() => setProjectType(type.value)}
                                    className='sr-only'
                                />

                                <Icon className='size-5' strokeWidth={1.75} aria-hidden='true' />

                                <span>
                                    {type.label}
                                </span>

                                <span className={cn('font-normal', checked ? 'text-white/80' : 'text-white/55')}>
                                    {type.detail}
                                </span>
                            </label>
                        )
                    })}
                </div>
            </fieldset>

            <div className='grid gap-4 sm:grid-cols-2'>
                <div>
                    <label htmlFor={fieldId('name')} className='mb-1.5 block text-sm font-medium text-white/85'>
                        Your name
                    </label>

                    <input
                        id={fieldId('name')}
                        name='name'
                        type='text'
                        autoComplete='name'
                        maxLength={FIELD_LIMITS.name}
                        value={values.name}
                        onChange={onChange}
                        aria-invalid={errors.name ? true : undefined}
                        aria-describedby={errors.name ? `${fieldId('name')}-error` : undefined}
                        className={inputClasses}
                    />

                    {errorFor('name')}
                </div>

                <div>
                    <label htmlFor={fieldId('email')} className='mb-1.5 block text-sm font-medium text-white/85'>
                        Email address
                    </label>

                    <input
                        id={fieldId('email')}
                        name='email'
                        type='email'
                        autoComplete='email'
                        inputMode='email'
                        maxLength={FIELD_LIMITS.email}
                        placeholder='you@company.com'
                        value={values.email}
                        onChange={onChange}
                        aria-invalid={errors.email ? true : undefined}
                        aria-describedby={errors.email ? `${fieldId('email')}-error` : undefined}
                        className={inputClasses}
                    />

                    {errorFor('email')}
                </div>

                <div className='sm:col-span-2'>
                    <label htmlFor={fieldId('message')} className='mb-1.5 block text-sm font-medium text-white/85'>
                        Tell me about your project
                    </label>

                    <textarea
                        id={fieldId('message')}
                        name='message'
                        rows={5}
                        maxLength={FIELD_LIMITS.message}
                        placeholder='Goals, timeline, budget, anything that helps.'
                        value={values.message}
                        onChange={onChange}
                        aria-invalid={errors.message ? true : undefined}
                        aria-describedby={errors.message ? `${fieldId('message')}-error` : undefined}
                        className={cn(inputClasses, 'min-h-[8.5rem] resize-y')}
                    />

                    {errorFor('message')}
                </div>
            </div>

            <div aria-hidden='true' className='absolute -left-[10000px] top-0 h-px w-px overflow-hidden'>
                <label>
                    Company
                    <input type='text' name='company' tabIndex={-1} autoComplete='off' defaultValue='' />
                </label>
            </div>

            <div className='mt-6 flex flex-col items-center gap-3'>
                <button
                    type='submit'
                    disabled={status === 'submitting'}
                    className={buttonClasses('primary', 'w-full sm:w-auto sm:min-w-[12rem]')}
                >
                    <span>
                        {status === 'submitting' ? 'Sending...' : 'Send message'}
                    </span>

                    {status !== 'submitting' && <ArrowRight className='size-4' strokeWidth={1.75} aria-hidden='true' />}
                </button>

                {formError && (
                    <p role='alert' className='w-full rounded-control bg-[#ffdedd] px-3 py-2.5 text-center text-sm leading-[1.3] text-brand-night'>
                        {formError}
                    </p>
                )}
            </div>
        </form>
    )
}
